<script setup lang="ts">
import axios from 'axios'
import { notifyError } from '@/utils/helpers'
import type { ECOption } from '@/plugins/echarts'

definePage({
    meta: {
        id: 'capteurs.compteurseau',
        title: "Compteurs d'eau",
        icon: 'mdi-counter',
        drawerIndex: 7,
        requiresAuth: true,
        requiresSiteAccess: 'agri.capteurs'
    },
})

const TB_BASE_URL = import.meta.env.VITE_TB_URL
const theme = useTheme()
const { smAndDown } = useDisplay()
const isLoading = ref(false)

// ── ThingsBoard auth ──────────────────────────────────────────────────────────

const tbToken = ref<string | null>(null)
const tbAuthority = ref<string | null>(null)
const loginError = ref('')

async function getTbToken(): Promise<string | null> {
    if (tbToken.value) return tbToken.value
    try {
        const response = await apiOmni.post('api/tb/login')
        tbToken.value = response.data.token
        void loadTbAuthority()
        return tbToken.value
    } catch (e: unknown) {
        const err = e as { response?: { status: number; data: unknown }; message?: string }
        loginError.value = err.response
            ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
            : (err.message ?? String(e))
        notifyError(loginError.value)
        return null
    }
}

function tbHeaders(token: string): Record<string, string> {
    return {
        'X-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
}

// ThingsBoard n'autorise l'écriture d'entité qu'aux administrateurs de tenant : sans ce test,
// les utilisateurs client verraient un bouton « Renommer » qui échoue systématiquement en 403.
async function loadTbAuthority() {
    if (!tbToken.value || tbAuthority.value) return
    try {
        const resp = await axios.get(`${TB_BASE_URL}/api/auth/user`, { headers: tbHeaders(tbToken.value) })
        tbAuthority.value = resp.data?.authority ?? null
    } catch {
        tbAuthority.value = null
    }
}

const peutRenommer = computed(() => tbAuthority.value === 'TENANT_ADMIN')

// ── Types ─────────────────────────────────────────────────────────────────────

declare interface TbDeviceId {
    id: string
    entityType: string
}

declare interface TbDevice {
    id: TbDeviceId
    name: string
    type: string
    label: string
    deviceProfileId: { id: string; entityType: string }
    createdTime: number
    additionalInfo?: Record<string, unknown>
}

declare interface TbPageData<T> {
    data: T[]
    totalPages: number
    totalElements: number
    hasNext: boolean
}

declare interface MonthlyConsumption {
    month: number
    label: string
    value: number | null
    daysOfUsage: number | null
}

declare interface DailyConsumption {
    day: number
    value: number | null
    hasUsage: boolean
    prevCumul: number | null
}

declare interface HourlyConsumption {
    hour: number
    value: number | null
}

// ── State ─────────────────────────────────────────────────────────────────────

/** Invisible filter — fetch both WMETER and WMCELL device types */
const DEVICE_TYPES = ['WMETER', 'Cell-Watermeter']

const allDevices = ref<TbDevice[]>([])
const selectedDevice = ref<TbDevice | null>(null)
const activeId = ref<string | null>(null)
const deviceSearch = ref('')
const isLoadingChart = ref(false)
const monthlyConsumption = ref<MonthlyConsumption[]>([])
const latestCumul = ref<number | null>(null)
const latestCurrentFlow = ref<number | null>(null)
const latestBatteryVoltage = ref<number | null>(null)
const latestRssi = ref<number | null>(null)
const latestSnr = ref<number | null>(null)
const latestTs = ref<number | null>(null)
const isLoadingTelemetry = ref(false)

const currentYear = new Date().getFullYear()
const selectedYear = ref(currentYear)
const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i)
const monthNames = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
const fullMonthNames = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']

// ── Calendar state ───────────────────────────────────────────────────────────

const calendarMonth = ref<number | null>(null)
const calendarYear = ref<number | null>(null)
const dailyData = ref<DailyConsumption[]>([])
const isLoadingCalendar = ref(false)

// Offset for first day of month (0 = Mon … 6 = Sun, French convention)
const calendarFirstDow = computed(() => {
    if (!calendarYear.value || !calendarMonth.value) return 0
    const d = new Date(calendarYear.value, calendarMonth.value - 1, 1).getDay()
    return d === 0 ? 6 : d - 1
})

// ── Hourly chart state ────────────────────────────────────────────────────────

const selectedDay = ref<number | null>(null)
const hourlyData = ref<HourlyConsumption[]>([])
const isLoadingHourly = ref(false)

const calendarCardRef = ref<HTMLElement | null>(null)
const hourlyCardRef = ref<HTMLElement | null>(null)

const hourlyChartOption = computed<ECOption>(() => {
    if (hourlyData.value.length === 0) return {}
    const labels = hourlyData.value.map(h => `${String(h.hour).padStart(2, '0')}h`)
    const values = hourlyData.value.map(h => h.value)
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            confine: false,
            enterable: false,
            axisPointer: { type: 'shadow' },
            position(point: number[]) {
                return [point[0] + 12, point[1] - 36]
            },
            formatter(params: unknown) {
                const items = params as Array<{ axisValueLabel: string; value: number | null; marker: string }>
                if (!items.length) return ''
                const p = items[0]
                return `${p.marker}<b>${p.axisValueLabel}</b>: ${p.value !== null ? p.value.toFixed(3) + ' m³' : '—'}`
            },
        },
        grid: { top: 24, left: '2%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: labels, axisTick: { alignWithLabel: true } },
        yAxis: { type: 'value', name: 'm³' },
        series: [{
            type: 'bar',
            data: values,
            itemStyle: { borderRadius: [3, 3, 0, 0] },
            label: {
                show: true,
                position: 'top',
                formatter: (p: { value: number | null }) =>
                    p.value !== null && p.value > 0 ? p.value.toFixed(2) : '',
                fontSize: 9,
            },
        }],
    }
})

// ── Duration helper ───────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000)
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} h ${minutes % 60} min`
    const days = Math.floor(hours / 24)
    return `${days} j ${hours % 24} h`
}

const timeSinceLastTelemetry = computed(() =>
    latestTs.value ? formatDuration(Date.now() - latestTs.value) : null
)

// ── Filtered list ─────────────────────────────────────────────────────────────

const filteredDevices = computed<TbDevice[]>(() => {
    const q = deviceSearch.value.trim().toLowerCase()
    if (!q) return allDevices.value
    return allDevices.value.filter(d =>
        (d.label || d.name).toLowerCase().includes(q) ||
        d.name.toLowerCase().includes(q),
    )
})

// ── Monthly consumption chart ────────────────────────────────────────────────

const monthlyChartOption = computed<ECOption>(() => {
    if (monthlyConsumption.value.length === 0) return {}
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            confine: true,
            axisPointer: { type: 'shadow' },
            formatter(params: unknown) {
                const items = params as Array<{ seriesName: string; value: number | null; marker: string; axisValueLabel: string }>
                if (!items.length) return ''
                let html = `<b>${items[0].axisValueLabel}</b><br/>`
                for (const p of items) {
                    if (p.value == null) continue
                    const isDay = p.seriesName === "Jours d'usage"
                    const valStr = isDay ? String(p.value) : p.value.toFixed(2)
                    const unit = isDay ? ' j' : ' m³'
                    html += `${p.marker}${p.seriesName}: <b>${valStr}${unit}</b><br/>`
                }
                return html
            },
        },
        legend: { top: 4 },
        grid: { top: 52, left: '2%', right: '5%', bottom: '3%', containLabel: true },
        xAxis: {
            type: 'category',
            data: monthlyConsumption.value.map(m => m.label),
            axisTick: { alignWithLabel: true },
        },
        yAxis: [
            { type: 'value', name: 'm³' },
            { type: 'value', name: 'jours', min: 0, splitLine: { show: false } },
        ],
        series: [
            {
                name: 'Consommation',
                type: 'bar',
                yAxisIndex: 0,
                data: monthlyConsumption.value.map(m => m.value),
                itemStyle: { borderRadius: [4, 4, 0, 0] },
                label: {
                    show: true,
                    position: 'top',
                    formatter: (p: { value: number | null }) =>
                        p.value !== null && p.value > 0 ? p.value.toFixed(1) : '',
                    fontSize: 10,
                },
            },
            {
                name: "Jours d'usage",
                type: 'bar',
                yAxisIndex: 1,
                data: monthlyConsumption.value.map(m => m.daysOfUsage),
                itemStyle: { borderRadius: [4, 4, 0, 0] },
                label: {
                    show: true,
                    position: 'top',
                    formatter: (p: { value: number | null }) =>
                        p.value !== null && p.value > 0 ? String(p.value) : '',
                    fontSize: 10,
                },
            },
        ],
    }
})

// ── Fetch WMETER devices (paginated, filtered server-side) ────────────────────

async function loadLatestTelemetry(deviceId: string) {
    isLoadingTelemetry.value = true
    latestCumul.value = null
    latestCurrentFlow.value = null
    latestBatteryVoltage.value = null
    latestRssi.value = null
    latestSnr.value = null
    latestTs.value = null
    try {
        const token = await getTbToken()
        if (!token) return
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
            `?keys=data_Cumul,data_CurrentFlow,data_BatV,rssi,snr`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        latestCumul.value = resp.data?.data_Cumul?.[0]?.value !== undefined
            ? parseFloat(resp.data.data_Cumul[0].value) : null
        latestCurrentFlow.value = resp.data?.data_CurrentFlow?.[0]?.value !== undefined
            ? parseFloat(resp.data.data_CurrentFlow[0].value) : null
        latestBatteryVoltage.value = resp.data?.data_BatV?.[0]?.value !== undefined
            ? parseFloat(resp.data.data_BatV[0].value) : null
        latestRssi.value = resp.data?.rssi?.[0]?.value !== undefined
            ? parseFloat(resp.data.rssi[0].value) : null
        latestSnr.value = resp.data?.snr?.[0]?.value !== undefined
            ? parseFloat(resp.data.snr[0].value) : null
        latestTs.value = resp.data?.data_Cumul?.[0]?.ts ?? null
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingTelemetry.value = false
    }
}

// ── Fetch WMETER + WMCELL devices (parallel, type-filtered) ─────────────────────

async function fetchByType(token: string, type: string): Promise<TbDevice[]> {
    const PAGE_SIZE = 100
    let page = 0
    const result: TbDevice[] = []
    while (true) {
        const url =
            `${TB_BASE_URL}/api/tenant/devices` +
            `?pageSize=${PAGE_SIZE}&page=${page}&type=${encodeURIComponent(type)}`
        const response = await axios.get<TbPageData<TbDevice>>(url, { headers: tbHeaders(token) })
        result.push(...response.data.data)
        if (!response.data.hasNext) break
        page++
    }
    return result
}

async function fetchWmeterDevices(token: string): Promise<TbDevice[]> {
    const results = await Promise.all(DEVICE_TYPES.map(t => fetchByType(token, t)))
    return results
        .flat()
        .sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
}

// ── Days of usage ────────────────────────────────────────────────────────────

async function fetchDaysOfUsage(
    token: string,
    deviceId: string,
    startTs: number,
    endTs: number,
    seedVal: number | null,
): Promise<number> {
    const url =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=data_Cumul&startTs=${startTs}&endTs=${endTs}&limit=10000&orderBy=ASC`
    const resp = await axios.get(url, { headers: tbHeaders(token) })
    const readings: { ts: number; value: string }[] = resp.data?.data_Cumul ?? []
    if (readings.length === 0) return 0
    const usageDays = new Set<string>()
    let prevVal = seedVal
    for (const r of readings) {
        const v = parseFloat(r.value)
        if (prevVal !== null && v > prevVal) {
            usageDays.add(new Date(r.ts).toISOString().slice(0, 10))
        }
        prevVal = v
    }
    return usageDays.size
}

// ── Monthly telemetry fetch ──────────────────────────────────────────────────

async function fetchMonthlyConsumption(token: string, deviceId: string, year: number): Promise<MonthlyConsumption[]> {
    const now = new Date()
    const isCurrentYear = year === now.getFullYear()
    // For current year: fetch up to current month; for past years: all 12 months
    const lastKnownMonth = isCurrentYear ? now.getMonth() + 1 : 12

    // Fetch cumulative reading at the start of each period:
    // index 0 = Dec of previous year (baseline), 1..lastKnownMonth = start of each month
    const cumulReadings: (number | null)[] = []
    for (let m = 0; m <= lastKnownMonth; m++) {
        let startTs: Date
        let endTs: Date
        if (m === 0) {
            startTs = new Date(year - 1, 11, 1, 12, 0, 0, 0)
            endTs = new Date(year, 0, 1)
        } else {
            startTs = new Date(year, m - 1, 1, 12, 0, 0, 0)
            endTs = new Date(year, m, 1)
        }
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
            `?keys=data_Cumul&startTs=${startTs.getTime()}&endTs=${endTs.getTime()}&limit=1`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        const raw = resp.data?.data_Cumul?.[0]?.value
        cumulReadings.push(raw !== undefined ? parseFloat(raw) : null)
    }

    // For the current month in current year, use the latest reading
    let latestCumulValue: number | null = null
    if (isCurrentYear) {
        const latestUrl =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries?keys=data_Cumul`
        const latestResp = await axios.get(latestUrl, { headers: tbHeaders(token) })
        const raw = latestResp.data?.data_Cumul?.[0]?.value
        latestCumulValue = raw !== undefined ? parseFloat(raw) : null
    }


    // Parallel: count days of usage per known month
    const daysOfUsageArr = await Promise.all(
        Array.from({ length: lastKnownMonth }, (_, i) => {
            const m = i + 1
            return fetchDaysOfUsage(
                token,
                deviceId,
                new Date(year, m - 1, 1).getTime(),
                new Date(year, m, 1).getTime(),
                cumulReadings[m - 1],
            )
        })
    )

    const result: MonthlyConsumption[] = []
    for (let m = 1; m <= 12; m++) {
        if (m > lastKnownMonth) {
            result.push({ month: m, label: monthNames[m], value: null, daysOfUsage: null })
            continue
        }
        const prevCumul = cumulReadings[m - 1]
        const currCumul = m < lastKnownMonth
            ? cumulReadings[m]
            : (isCurrentYear ? latestCumulValue : cumulReadings[m])
        if (prevCumul === null || currCumul === null) {
            result.push({ month: m, label: monthNames[m], value: null, daysOfUsage: daysOfUsageArr[m - 1] })
        } else {
            result.push({
                month: m,
                label: monthNames[m],
                value: Math.max(0, parseFloat((currCumul - prevCumul).toFixed(2))),
                daysOfUsage: daysOfUsageArr[m - 1],
            })
        }
    }
    return result
}

// ── Daily consumption (calendar drill-down) ──────────────────────────────────

async function fetchDailyConsumption(token: string, deviceId: string, year: number, month: number): Promise<DailyConsumption[]> {
    const startTs = new Date(year, month - 1, 1).getTime()
    const endTs = new Date(year, month, 1).getTime()
    // Seed: last reading before the month
    const seedUrl =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=data_Cumul&endTs=${startTs}&limit=1`
    const seedResp = await axios.get(seedUrl, { headers: tbHeaders(token) })
    const seedRaw = seedResp.data?.data_Cumul?.[0]?.value
    let prevVal: number | null = seedRaw !== undefined ? parseFloat(seedRaw) : null
    // All readings for the month ASC
    const url =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=data_Cumul&startTs=${startTs}&endTs=${endTs}&limit=10000&orderBy=ASC`
    const resp = await axios.get(url, { headers: tbHeaders(token) })
    const readings: { ts: number; value: string }[] = resp.data?.data_Cumul ?? []
    // Last cumul value per calendar day
    const dayMap = new Map<number, number>()
    for (const r of readings) {
        dayMap.set(new Date(r.ts).getDate(), parseFloat(r.value))
    }
    const daysInMonth = new Date(year, month, 0).getDate()
    const result: DailyConsumption[] = []
    for (let d = 1; d <= daysInMonth; d++) {
        const dayVal = dayMap.get(d) ?? null
        if (dayVal !== null && prevVal !== null) {
            const consumption = Math.max(0, parseFloat((dayVal - prevVal).toFixed(3)))
            result.push({ day: d, value: consumption, hasUsage: consumption > 0, prevCumul: prevVal })
        } else {
            result.push({ day: d, value: null, hasUsage: false, prevCumul: prevVal })
        }
        if (dayVal !== null) prevVal = dayVal
    }
    return result
}

async function fetchHourlyConsumption(
    token: string,
    deviceId: string,
    year: number,
    month: number,
    day: number,
    seed: number | null,
): Promise<HourlyConsumption[]> {
    const dayStart = new Date(year, month - 1, day).getTime()
    const dayEnd = new Date(year, month - 1, day + 1).getTime()
    let prevVal: number | null = seed
    // All readings for the day ASC
    const resp = await axios.get(
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=data_Cumul&startTs=${dayStart}&endTs=${dayEnd}&limit=10000&orderBy=ASC`,
        { headers: tbHeaders(token) },
    )
    const readings: { ts: number; value: string }[] = resp.data?.data_Cumul ?? []
    // Last cumul value per hour
    const hourMap = new Map<number, number>()
    for (const r of readings) {
        hourMap.set(new Date(r.ts).getHours(), parseFloat(r.value))
    }
    const result: HourlyConsumption[] = []
    for (let h = 0; h < 24; h++) {
        const hVal = hourMap.get(h) ?? null
        if (hVal !== null && prevVal !== null) {
            result.push({ hour: h, value: Math.max(0, parseFloat((hVal - prevVal).toFixed(3))) })
        } else {
            result.push({ hour: h, value: null })
        }
        if (hVal !== null) prevVal = hVal
    }
    return result
}

async function openDayChart(day: number) {
    if (!selectedDevice.value || !calendarYear.value || !calendarMonth.value) return
    selectedDay.value = day
    isLoadingHourly.value = true
    hourlyData.value = []
    await nextTick()
    ;(hourlyCardRef.value as any)?.$el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    try {
        const token = await getTbToken()
        if (!token) return
        const seed = dailyData.value.find(d => d.day === day)?.prevCumul ?? null
        hourlyData.value = await fetchHourlyConsumption(
            token, selectedDevice.value.id.id,
            calendarYear.value, calendarMonth.value, day, seed,
        )
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingHourly.value = false
    }
}

async function openMonthCalendar(month: number) {
    if (!selectedDevice.value) return
    calendarMonth.value = month
    calendarYear.value = selectedYear.value
    selectedDay.value = null
    hourlyData.value = []
    isLoadingCalendar.value = true
    dailyData.value = []
    await nextTick()
    ;(calendarCardRef.value as any)?.$el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    try {
        const token = await getTbToken()
        if (!token) return
        dailyData.value = await fetchDailyConsumption(token, selectedDevice.value.id.id, selectedYear.value, month)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingCalendar.value = false
    }
}

function onMonthClick(params: { dataIndex: number }) {
    openMonthCalendar(params.dataIndex + 1)
}

async function loadMonthlyChart(deviceId: string) {
    monthlyConsumption.value = []
    isLoadingChart.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        monthlyConsumption.value = await fetchMonthlyConsumption(token, deviceId, selectedYear.value)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingChart.value = false
    }
}

watch(selectedYear, () => {
    if (selectedDevice.value) loadMonthlyChart(selectedDevice.value.id.id)
})

// ── Selection ─────────────────────────────────────────────────────────────────

function onSelectDevice(device: TbDevice) {
    activeId.value = device.id.id
    selectedDevice.value = device
    monthlyConsumption.value = []
    calendarMonth.value = null
    dailyData.value = []
    selectedDay.value = null
    hourlyData.value = []
    pickerOpen.value = false
    loadLatestTelemetry(device.id.id)
    loadMonthlyChart(device.id.id)
}

// ── Sélecteur mobile ─────────────────────────────────────────────────

const pickerOpen = ref(false)

// ── Load ──────────────────────────────────────────────────────────────────────

async function loadDevices() {
    loginError.value = ''
    isLoading.value = true
    selectedDevice.value = null
    activeId.value = null
    try {
        const token = await getTbToken()
        if (!token) return
        allDevices.value = await fetchWmeterDevices(token)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

onMounted(loadDevices)

// ── Renommer un compteur ─────────────────────────────────────────────

const renameDialog = ref(false)
const renameLabel = ref('')
const isSavingLabel = ref(false)

function openRenameDialog() {
    if (!selectedDevice.value) return
    renameLabel.value = selectedDevice.value.label || ''
    renameDialog.value = true
}

async function saveLabel() {
    const device = selectedDevice.value
    if (!device) return
    const label = renameLabel.value.trim()
    isSavingLabel.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        // ThingsBoard remplace l'entité complète : on repart de la version courante du serveur
        // pour ne pas écraser les champs modifiés ailleurs entre-temps.
        const current = await axios.get<TbDevice>(
            `${TB_BASE_URL}/api/device/${device.id.id}`,
            { headers: tbHeaders(token) },
        )
        const resp = await axios.post<TbDevice>(
            `${TB_BASE_URL}/api/device`,
            { ...current.data, label },
            { headers: tbHeaders(token) },
        )
        device.label = resp.data.label
        renameDialog.value = false
        Notify.success('Nom du compteur modifié avec succès')
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingLabel.value = false
    }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(ms: number): string {
    return new Date(ms).toLocaleDateString('fr-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}
</script>

<template>
    <v-card class="page-card" :loading="isLoading">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px; min-width: 0;">
                    <v-avatar v-if="!smAndDown" color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="mdi-counter" size="26" />
                    </v-avatar>
                    <div v-if="!smAndDown">
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Compteurs d'eau</div>
                    </div>
                    <!-- Sur mobile l'arbre est masqué : la sélection passe par cette puce. -->
                    <v-chip
                        v-else
                        class="selecteur-chip"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi-water-pump"
                        append-icon="mdi-chevron-down"
                        @click="pickerOpen = true"
                    >
                        {{ selectedDevice ? (selectedDevice.label || selectedDevice.name) : 'Choisir un compteur' }}
                    </v-chip>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn
                        color="primary"
                        variant="tonal"
                        rounded="pill"
                        :icon="smAndDown ? 'mdi-refresh' : undefined"
                        :prepend-icon="smAndDown ? undefined : 'mdi-refresh'"
                        :size="smAndDown ? 'small' : undefined"
                        :loading="isLoading"
                        @click="loadDevices"
                    >
                        <template v-if="!smAndDown">Rafraîchir</template>
                    </v-btn>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Error banner -->
        <v-alert
            v-if="loginError"
            type="error"
            variant="tonal"
            density="compact"
            class="mx-4 mt-3 mb-0 flex-shrink-0"
            rounded="lg"
        >
            {{ loginError }}
        </v-alert>

        <!-- Main content: tree + detail panel -->
        <div class="d-flex" style="flex: 1 1 auto; overflow: hidden; min-height: 0;">

            <!-- Left: device list tree (desktop) -->
            <div v-if="!smAndDown" class="tree-panel d-flex flex-column">

                <!-- Search -->
                <div class="pa-3">
                    <v-text-field
                        v-model="deviceSearch"
                        label="Rechercher un compteur"
                        density="compact"
                        variant="outlined"
                        rounded="lg"
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                    />
                </div>

                <v-divider />

                <!-- Tree -->
                <div style="flex: 1 1 auto; overflow-y: auto;">
                    <v-list density="compact" nav>
                        <v-list-item
                            v-for="device in filteredDevices"
                            :key="device.id.id"
                            density="compact"
                            rounded="lg"
                            :active="activeId === device.id.id"
                            color="primary"
                            @click="onSelectDevice(device)"
                        >
                            <template #prepend>
                                <v-icon icon="mdi-water-pump" size="16" color="primary" class="mr-2" />
                            </template>
                            <v-list-item-title class="text-caption font-weight-medium">
                                {{ device.label || device.name }}
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="device.label" class="text-caption">
                                {{ device.name }}
                            </v-list-item-subtitle>
                        </v-list-item>
                    </v-list>

                    <div
                        v-if="!isLoading && filteredDevices.length === 0 && !loginError"
                        class="pa-4 text-center text-medium-emphasis text-caption"
                    >
                        Aucun compteur trouvé
                    </div>
                </div>
            </div>

            <!-- Right: device detail -->
            <div class="detail-panel">
                <!-- Placeholder -->
                <template v-if="!selectedDevice">
                    <div
                        class="d-flex flex-column align-center justify-center text-medium-emphasis pa-4"
                        style="height: 100%; gap: 12px;"
                    >
                        <v-icon icon="mdi-gesture-tap" size="48" />
                        <span class="text-body-2 text-center">
                            {{ smAndDown ? 'Touchez « Choisir un compteur » ci-dessus' : 'Sélectionnez un compteur dans la liste' }}
                        </span>
                        <span v-if="!isLoading && allDevices.length === 0 && !loginError" class="text-body-2 text-center">
                            Contactez-nous pour avoir ces capteurs sur votre ferme !
                            <a href="https://www.agrimetrie.com/compteur-deau" target="_blank" rel="noopener noreferrer">
                                Cliquez ici pour plus d'informations.
                            </a>
                        </span>
                    </div>
                </template>

                <!-- Detail card -->
                <template v-else>
                    <div class="detail-scroll">
                    <v-card variant="outlined" rounded="lg">
                        <v-card-title class="d-flex align-center flex-wrap" style="gap: 10px;">
                            <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
                                <v-icon icon="mdi-water-pump" size="20" />
                            </v-avatar>
                            <div style="flex: 1 1 140px; min-width: 0;">
                                <div class="text-subtitle-1 font-weight-bold text-truncate">
                                    {{ selectedDevice.label || selectedDevice.name }}
                                </div>
                                <div v-if="selectedDevice.label" class="text-caption text-medium-emphasis text-truncate">
                                    {{ selectedDevice.name }}
                                </div>
                            </div>
                            <!-- Diagnostics chips -->
                            <div v-if="!isLoadingTelemetry" class="d-flex align-center flex-wrap" style="gap: 6px;">
                                <v-chip
                                    v-if="latestBatteryVoltage !== null"
                                    size="x-small"
                                    :color="latestBatteryVoltage >= 3.5 ? 'success' : latestBatteryVoltage >= 3.2 ? 'warning' : 'error'"
                                    variant="tonal"
                                    :prepend-icon="latestBatteryVoltage >= 3.5 ? 'mdi-battery-high' : latestBatteryVoltage >= 3.2 ? 'mdi-battery-medium' : 'mdi-battery-low'"
                                >
                                    {{ latestBatteryVoltage.toFixed(2) }} V
                                </v-chip>
                                <v-chip
                                    v-if="latestRssi !== null"
                                    size="x-small"
                                    :color="latestRssi >= -70 ? 'success' : latestRssi >= -85 ? 'warning' : 'error'"
                                    variant="tonal"
                                    :prepend-icon="latestRssi >= -70 ? 'mdi-signal-cellular-3' : latestRssi >= -85 ? 'mdi-signal-cellular-2' : 'mdi-signal-cellular-1'"
                                >
                                    {{ latestRssi }} dBm
                                </v-chip>
                                <v-chip
                                    v-if="latestSnr !== null"
                                    size="x-small"
                                    color="secondary"
                                    variant="tonal"
                                    prepend-icon="mdi-sine-wave"
                                >
                                    {{ latestSnr.toFixed(1) }} dB
                                </v-chip>
                            </div>
                            <v-progress-circular v-else size="16" width="2" indeterminate color="primary" />

                            <v-menu v-if="peutRenommer">
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" size="small" />
                                </template>
                                <v-list density="compact">
                                    <v-list-item
                                        prepend-icon="mdi-pencil-outline"
                                        title="Renommer le compteur"
                                        @click="openRenameDialog"
                                    />
                                </v-list>
                            </v-menu>
                        </v-card-title>

                        <v-divider />

                        <v-card-text class="pa-3">
                            <!-- Type chip -->
                            <div class="mb-3">
                                <v-chip size="small" color="primary" variant="tonal" prepend-icon="mdi-tag-outline">
                                    {{ selectedDevice.type || '—' }}
                                </v-chip>
                            </div>

                            <!-- Latest telemetry -->
                            <div v-if="isLoadingTelemetry" class="d-flex align-center" style="gap: 8px;">
                                <v-progress-circular size="16" width="2" indeterminate color="primary" />
                                <span class="text-caption text-medium-emphasis">Chargement...</span>
                            </div>
                            <div v-else class="d-flex flex-wrap align-start" style="gap: 16px;">
                                <!-- Cumulative -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Cumulatif</div>
                                    <div class="text-h5 font-weight-bold d-flex align-end" style="gap: 4px; line-height: 1.1;">
                                        <span>{{ latestCumul !== null ? latestCumul.toFixed(2) : '\u2014' }}</span>
                                        <span v-if="latestCumul !== null" class="text-body-2 text-medium-emphasis pb-1">m³</span>
                                    </div>
                                </div>

                                <v-divider vertical style="height: 48px; align-self: center;" />

                                <!-- Current flow -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Débit actuel</div>
                                    <div class="text-h5 font-weight-bold d-flex align-end" style="gap: 4px; line-height: 1.1;">
                                        <span>{{ latestCurrentFlow !== null ? latestCurrentFlow.toFixed(3) : '\u2014' }}</span>
                                        <span v-if="latestCurrentFlow !== null" class="text-body-2 text-medium-emphasis pb-1">m³/h</span>
                                    </div>
                                </div>

                                <v-divider vertical style="height: 48px; align-self: center;" />

                                <!-- Last seen -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Dernière donnée</div>
                                    <div class="text-body-2 font-weight-bold">{{ timeSinceLastTelemetry ?? '\u2014' }}</div>
                                    <div v-if="latestTs" class="text-caption text-medium-emphasis">
                                        {{ new Date(latestTs).toLocaleString('fr-CA') }}
                                    </div>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>

                    <!-- Monthly consumption bar chart -->
                    <v-card variant="outlined" rounded="lg" class="mt-4" :class="{ 'mb-0': calendarMonth !== null }">
                        <v-card-title class="text-subtitle-2 py-2 px-4 d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon icon="mdi-chart-bar" size="18" />
                                Consommation mensuelle
                            </div>
                            <v-btn-toggle
                                v-model="selectedYear"
                                density="compact"
                                variant="outlined"
                                color="primary"
                                mandatory
                                rounded="lg"
                            >
                                <v-btn
                                    v-for="y in availableYears"
                                    :key="y"
                                    :value="y"
                                    size="small"
                                    :style="smAndDown ? 'min-width: 44px;' : 'min-width: 52px;'"
                                >
                                    {{ y }}
                                </v-btn>
                            </v-btn-toggle>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-2">
                            <div v-if="isLoadingChart"
                                class="d-flex align-center justify-center"
                                style="height: 200px;">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <div v-else-if="monthlyConsumption.length === 0"
                                class="d-flex align-center justify-center text-medium-emphasis text-caption"
                                style="height: 200px;">
                                Aucune donnée disponible
                            </div>
                            <VChart v-else
                                :option="monthlyChartOption"
                                autoresize
                                style="height: 240px; width: 100%; cursor: pointer;"
                                @click="onMonthClick"
                            />
                        </v-card-text>
                    </v-card>
                    <!-- Daily calendar (inline, shown after clicking a month) -->
                    <v-card ref="calendarCardRef" v-if="calendarMonth !== null" variant="outlined" rounded="lg" class="mt-4">
                        <v-card-title class="d-flex align-center justify-space-between py-2 px-4" style="gap: 8px;">
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon icon="mdi-calendar-month" size="18" />
                                {{ fullMonthNames[calendarMonth] }} {{ calendarYear }}
                            </div>
                            <v-btn icon="mdi-close" variant="text" size="x-small" @click="calendarMonth = null" />
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-3">
                            <div v-if="isLoadingCalendar" class="d-flex align-center justify-center" style="height: 160px;">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <template v-else>
                                <!-- Day-of-week headers -->
                                <div class="calendar-grid mb-1">
                                    <div v-for="h in ['L', 'M', 'M', 'J', 'V', 'S', 'D']" :key="h"
                                        class="text-caption text-medium-emphasis text-center font-weight-bold py-1">
                                        {{ h }}
                                    </div>
                                </div>
                                <!-- Day cells -->
                                <div class="calendar-grid">
                                    <div v-for="n in calendarFirstDow" :key="'e' + n" />
                                    <div
                                        v-for="d in dailyData"
                                        :key="d.day"
                                        class="calendar-day rounded-lg pa-1 text-center"
                                        :class="[
                                            d.hasUsage ? 'calendar-day--usage' : d.value === null ? 'calendar-day--empty' : 'calendar-day--no-usage',
                                            d.hasUsage ? 'calendar-day--clickable' : '',
                                            selectedDay === d.day ? 'calendar-day--selected' : '',
                                        ]"
                                        @click="d.hasUsage ? openDayChart(d.day) : undefined"
                                    >
                                        <div class="text-caption font-weight-bold" style="line-height: 1.2;">{{ d.day }}</div>
                                        <div class="text-caption" style="font-size: 9px; line-height: 1.2; white-space: nowrap;">
                                            {{ d.value !== null ? d.value.toFixed(2) : '—' }}
                                        </div>
                                        <div v-if="d.value !== null" class="text-caption text-medium-emphasis" style="font-size: 8px; line-height: 1;">m³</div>
                                    </div>
                                </div>
                                <!-- Legend -->
                                <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
                                    <div class="d-flex align-center" style="gap: 4px;">
                                        <div class="calendar-legend-dot calendar-day--usage rounded" />
                                        <span class="text-caption text-medium-emphasis">Consommation</span>
                                    </div>
                                    <div class="d-flex align-center" style="gap: 4px;">
                                        <div class="calendar-legend-dot calendar-day--no-usage rounded" />
                                        <span class="text-caption text-medium-emphasis">Pas de variation</span>
                                    </div>
                                    <div class="d-flex align-center" style="gap: 4px;">
                                        <div class="calendar-legend-dot calendar-day--empty rounded" />
                                        <span class="text-caption text-medium-emphasis">Aucune donnée</span>
                                    </div>
                                </div>
                            </template>
                        </v-card-text>
                    </v-card>

                    <!-- Hourly chart (inline, shown after clicking a day) -->
                    <v-card ref="hourlyCardRef" v-if="selectedDay !== null" variant="outlined" rounded="lg" class="mt-4">
                        <v-card-title class="d-flex align-center justify-space-between py-2 px-4" style="gap: 8px;">
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon icon="mdi-chart-timeline-variant" size="18" />
                                {{ selectedDay }} {{ calendarMonth !== null ? fullMonthNames[calendarMonth] : '' }} {{ calendarYear }}
                            </div>
                            <v-btn icon="mdi-close" variant="text" size="x-small" @click="selectedDay = null" />
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-2">
                            <div v-if="isLoadingHourly" class="d-flex align-center justify-center" style="height: 160px;">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <VChart v-else
                                :option="hourlyChartOption"
                                autoresize
                                style="height: 200px; width: 100%;"
                            />
                        </v-card-text>
                    </v-card>
                    </div>
                </template>
            </div>
        </div>
    </v-card>

    <!-- Sélecteur de compteur (mobile) -->
    <v-dialog v-model="pickerOpen" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card>
            <v-card-title class="d-flex align-center pt-4 px-4" style="gap: 10px;">
                <v-icon icon="mdi-counter" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Compteurs d'eau</span>
                <v-spacer />
                <v-btn icon="mdi-close" variant="text" size="small" @click="pickerOpen = false" />
            </v-card-title>
            <div class="px-4 pb-3">
                <v-text-field
                    v-model="deviceSearch"
                    label="Rechercher un compteur"
                    density="compact"
                    variant="outlined"
                    rounded="lg"
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                />
            </div>
            <v-divider />
            <v-card-text class="pa-0">
                <v-list density="compact" nav>
                    <v-list-item
                        v-for="device in filteredDevices"
                        :key="device.id.id"
                        rounded="lg"
                        :active="activeId === device.id.id"
                        color="primary"
                        @click="onSelectDevice(device)"
                    >
                        <template #prepend>
                            <v-icon icon="mdi-water-pump" size="18" color="primary" class="mr-2" />
                        </template>
                        <v-list-item-title class="text-body-2 font-weight-medium">
                            {{ device.label || device.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="device.label" class="text-caption">
                            {{ device.name }}
                        </v-list-item-subtitle>
                    </v-list-item>
                </v-list>
                <div
                    v-if="!isLoading && filteredDevices.length === 0"
                    class="pa-6 text-center text-medium-emphasis text-caption"
                >
                    Aucun compteur trouvé
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Renommer -->
    <v-dialog v-model="renameDialog" max-width="480" :fullscreen="smAndDown">
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon icon="mdi-pencil-outline" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Renommer le compteur</span>
            </v-card-title>
            <v-card-text class="px-5">
                <div class="text-caption text-medium-emphasis mb-3">
                    Identifiant technique : {{ selectedDevice?.name }}
                </div>
                <v-text-field
                    v-model="renameLabel"
                    label="Nom affiché"
                    variant="outlined"
                    density="compact"
                    rounded="lg"
                    hide-details
                    autofocus
                    @keyup.enter="saveLabel"
                />
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
                <v-spacer />
                <v-btn variant="text" rounded="pill" @click="renameDialog = false">Annuler</v-btn>
                <v-btn color="primary" variant="flat" rounded="pill" :loading="isSavingLabel" @click="saveLabel">
                    Enregistrer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
    flex-shrink: 0;
}

/* Comme la page Carte : la carte occupe tout le viewport, sans marge ni coins arrondis. */
.page-card {
    height: calc(100vh - var(--v-layout-top, 64px) - var(--v-layout-bottom, 0px));
    display: flex;
    flex-direction: column;
    border-radius: 0;
}

/* Le nom du compteur peut être long : la puce se tronque au lieu de pousser le bouton hors écran. */
.selecteur-chip {
    max-width: 55vw;
    transition: opacity 0.15s;
}
.selecteur-chip:hover {
    opacity: 0.78;
}
.selecteur-chip :deep(.v-chip__content) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tree-panel {
    flex-shrink: 0;
    width: 300px;
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.detail-panel {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
}

.detail-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;
    min-height: 0;
}

.sensor-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 80px;
}

/* ── Calendar ────────────────────────────── */
.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
}

.calendar-day {
    min-height: 46px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.calendar-day--usage {
    background: rgba(var(--v-theme-success), 0.18);
    color: rgb(var(--v-theme-success));
}

.calendar-day--clickable {
    cursor: pointer;
}

.calendar-day--clickable:hover {
    background: rgba(var(--v-theme-success), 0.32);
}

.calendar-day--selected {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
}

.calendar-day--no-usage {
    background: rgba(var(--v-border-color), 0.08);
}

.calendar-day--empty {
    background: transparent;
    opacity: 0.4;
}

.calendar-legend-dot {
    width: 12px;
    height: 12px;
}
</style>
